/**
 * RewardedAdButton — "Watch ad → earn Fantasy Dollars (₣)".
 *
 * Optional, voluntary rewarded ad that credits the user's global-league ranking
 * score. ₣ have no cash value (not a sweepstakes). On web the ad is simulated;
 * the production mobile build wires a real AppLovin MAX / AdMob rewarded ad +
 * server-side verification before the grant is issued.
 *
 * The per-ad payout is admin-configurable (Admin → Launch Prep → Rewarded Ads),
 * and early adopters (the first N registered users) earn a higher bonus amount.
 */
import { useEffect, useState } from 'react';
import { PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

// Realistic rewarded-video length. Real AdMob/AppLovin rewarded ads run
// 15–30s (30s is the most common) and CANNOT be skipped — you watch the whole
// thing before the reward unlocks. We mirror that so the web "house" rewarded
// ad feels identical to production mobile ads (and gives a real reason to buy
// ad-free Premium). The server still enforces its own min-watch anti-abuse
// floor via the begin/echo nonce.
const AD_SECONDS = 30;

export default function RewardedAdButton({ leagueId }) {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(AD_SECONDS);
  const [claiming, setClaiming] = useState(false);
  const [nonce, setNonce] = useState(null);

  const loadCfg = async () => {
    try {
      const r = await api.get('/ads/reward-config');
      setCfg(r.data);
    } catch {
      setCfg({ enabled: false });
    }
  };

  useEffect(() => { loadCfg(); }, []);

  // countdown while the simulated ad is "playing"
  useEffect(() => {
    if (!open || countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [open, countdown]);

  if (!cfg || !cfg.enabled) return null;

  const capReached = cfg.remaining === 0;
  const amount = Number(cfg.amount || 0);

  const startAd = async () => {
    if (capReached) {
      toast.info('You\'ve hit today\'s limit — come back tomorrow!');
      return;
    }
    try {
      const b = await api.post('/ads/reward/begin');
      setNonce(b.data.nonce);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start the ad — try again.');
      return;
    }
    setCountdown(AD_SECONDS);
    setOpen(true);
  };

  const claim = async () => {
    setClaiming(true);
    try {
      const r = await api.post('/ads/reward', { league_id: leagueId || null, nonce });
      toast.success(`+₣${Number(r.data.credited).toFixed(2)} added to your balance!`);
      setCfg((c) => ({
        ...c,
        watched_today: r.data.watched_today,
        remaining: r.data.remaining,
      }));
      // Refresh the user's spendable balance / net worth across the app.
      window.dispatchEvent(new CustomEvent('fws:team-refresh', { detail: r.data }));
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not grant reward');
    } finally {
      setClaiming(false);
      setNonce(null);
    }
  };

  const subtitle = cfg.daily_cap > 0
    ? `${cfg.remaining ?? cfg.daily_cap} of ${cfg.daily_cap} left today`
    : 'Unlimited';

  return (
    <>
      <button
        onClick={startAd}
        disabled={capReached}
        data-testid="watch-ad-row"
        className="w-full flex items-center justify-between px-4 py-3 border-t border-border/60 hover:bg-secondary/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
      >
        <span className="flex items-center gap-2 flex-wrap">
          <PlayCircle className="h-4 w-4 text-emerald-500" />
          <span className="text-sm font-medium">Watch an ad → earn ₣{amount.toFixed(2)}</span>
          {cfg.is_early_adopter && (
            <span className="text-[9px] font-bold uppercase tracking-wide bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 px-1.5 py-0.5 rounded-full" data-testid="early-adopter-badge">
              Early-adopter ✨
            </span>
          )}
        </span>
        <span className="text-xs text-muted-foreground" data-testid="watch-ad-remaining">{subtitle}</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => !claiming && setOpen(o)}>
        <DialogContent className="max-w-sm" data-testid="rewarded-ad-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              Earn ₣{amount.toFixed(2)}{cfg.is_early_adopter ? ' · early-adopter bonus ✨' : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative aspect-video w-full rounded-lg bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center overflow-hidden">
              <span className="absolute top-2 left-2 text-[9px] font-bold tracking-wider text-white/70 bg-black/40 px-1.5 py-0.5 rounded">ADVERTISEMENT</span>
              <div className="text-center text-white/80">
                <PlayCircle className="h-10 w-10 mx-auto mb-2 opacity-70" />
                <p className="text-xs">
                  {countdown > 0 ? `Your reward unlocks in ${countdown}s…` : 'Thanks for watching!'}
                </p>
              </div>
              {/* Playback progress bar — makes the house ad feel like a real
                  rewarded video counting down to the reward. */}
              <div
                className="absolute bottom-0 left-0 h-1 bg-emerald-500 transition-all duration-1000 ease-linear"
                style={{ width: `${((AD_SECONDS - countdown) / AD_SECONDS) * 100}%` }}
                data-testid="rewarded-ad-progress"
              />
            </div>
            <p className="text-[10px] text-muted-foreground text-center">
              Fantasy Dollars have no cash value and cannot be redeemed or transferred.
            </p>
            <Button
              onClick={claim}
              disabled={countdown > 0 || claiming}
              className="w-full"
              data-testid="claim-ad-reward-btn"
            >
              {claiming ? 'Claiming…' : countdown > 0 ? `Claim in ${countdown}s` : `Claim ₣${amount.toFixed(2)}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
